import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetOversightAssessmentsRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { oversightAssessmentAddUrl } from '@/utils/urls';

import { useGetCollectionTableProps } from './config';
import { OversightAssessmentRegisterToolbar } from './OversightAssessmentRegisterToolbar';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const OversightAssessmentsPage: FC = () => {
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();
  const { t: st } = useTranslation('common', {
    keyPrefix: 'complianceMonitoringAssessment',
  });
  const { data, loading } = useGetOversightAssessmentsRegister({
    queryArgs: {},
  });
  const { t } = useTranslation('common');
  const tableProps = useGetCollectionTableProps(data?.oversight_assessment);
  const assessmentCount = useMemo(() => {
    if (loading) {
      return '';
    }

    return `(${data?.oversight_assessment?.length})`;
  }, [data, loading]);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const title = t('navigationMenu.assessments.oversightRegisterTitle');
  const onCreate = useCallback(
    () => navigate(oversightAssessmentAddUrl()),
    [navigate]
  );

  return (
    <PageLayout
      helpTranslationKey={'complianceMonitoringAssessment.registerHelp'}
      title={title}
      counter={assessmentCount}
      // Object-level toolbar (CUS-164) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <OversightAssessmentRegisterToolbar
            title={title}
            counter={assessmentCount}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            onCreate={onCreate}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          <Permission permission={'insert:oversight_assessment'}>
            <Button variant={'primary'} href={oversightAssessmentAddUrl()}>
              {st('create_new_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.OversightAssessment}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
    </PageLayout>
  );
};

export default OversightAssessmentsPage;
